import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Typography,
  Box,
  IconButton,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, Replay as RestartIcon } from '@mui/icons-material';
import InvestorTypeFilter from '../InvestorTypeFilter/InvestorTypeFilter';
import { DEFAULT_SELECTED_COLUMNS_EXPLORER } from 'src/const';

interface Column {
  id: string;
  label: string;
  group: string;
}

const ALL_COLUMNS_SAVED_MARKET_LIST: Column[] = [
  // Basic Columns
  { id: 'regulation_status', label: 'Regulation', group: 'Basic Columns' },
  { id: 'occupancy_rate', label: 'Occupancy', group: 'Basic Columns' },
  { id: 'property_price_2bd', label: 'Property Price (2bd)', group: 'Basic Columns' },
  { id: 'short_term_rental_yield_2bd', label: 'STR Yield (2bd)', group: 'Basic Columns' },
  { id: 'rent_2bd', label: 'Rent Price (2bd)', group: 'Basic Columns' },
  { id: 'rent_gap_2bd', label: 'Rent Gap (2bd)', group: 'Basic Columns' },
  { id: 'supply', label: 'No. of listings', group: 'Basic Columns' },
  { id: 'adr', label: 'ADR', group: 'Basic Columns' },
  { id: 'revenue', label: 'Revenue', group: 'Basic Columns' },
  { id: 'yoy_supply_change', label: 'YoY Supply', group: 'Basic Columns' },
  { id: 'yoy_revenue_change', label: 'YoY RevPAR', group: 'Basic Columns' },
  { id: 'seasonality_graph', label: 'Seasonality Graph', group: 'Basic Columns' },

  // Advanced Columns
  { id: 'yoy_occupancy_change', label: 'YoY Occupancy', group: 'Advanced Columns' },
  { id: 'yoy_adr_change', label: 'YoY ADR', group: 'Advanced Columns' },
  { id: 'yoy_supply_change_2y', label: '2Y YoY Supply', group: 'Advanced Columns' },
  { id: 'yoy_revenue_change_2y', label: '2Y YoY Revenue', group: 'Advanced Columns' },
  { id: 'yoy_occupancy_change_2y', label: '2Y YoY Occupancy', group: 'Advanced Columns' },
  { id: 'yoy_adr_change_2y', label: '2Y YoY ADR', group: 'Advanced Columns' },
];

const DEFAULT_SELECTED_COLUMNS_SAVED_MARKET_LIST = [
  'regulation_status',
  'occupancy_rate',
  'supply',
  'adr',
  'revenue',
  'yoy_supply_change',
  'yoy_revenue_change',
  'seasonality_graph',
];

const ALL_COLUMNS_EXPLORER = [
  { id: 'regulation', label: 'Regulation', group: 'Basic Columns' },
  { id: 'supply', label: 'Number of listings', group: 'Basic Columns' },
  { id: 'short_term_rental_yield', label: 'Short-term rental yield', group: 'Basic Columns' },
  { id: 'rent_gap', label: 'Rent gap', group: 'Basic Columns' },
  { id: 'revenue', label: 'Average Revenue (2 BD)', group: 'Basic Columns' },
  { id: 'occupancy_rate', label: 'Occupancy Rate', group: 'Basic Columns' },
  { id: 'adr', label: 'ADR', group: 'Basic Columns' },
  { id: 'yoy_supply_change', label: 'YoY Supply Change', group: 'Advanced Columns' },
  { id: 'yoy_revenue_change', label: 'YoY Revenue Change', group: 'Advanced Columns' },
];

interface ColumnPickerProps {
  open: boolean;
  onClose: () => void;
  initialSelectedColumns: string[];
  onApplyColumns: (selectedColumns: string[]) => void;
  initialInvestorType: string;
  onApplyInvestorType: (investorType: string) => void;
  isExplorer?: boolean;
}

const ColumnPicker: React.FC<ColumnPickerProps> = ({
  open,
  onClose,
  initialSelectedColumns,
  onApplyColumns,
  initialInvestorType,
  onApplyInvestorType,
  isExplorer,
}) => {
  const [pendingSelectedColumns, setPendingSelectedColumns] = useState<string[]>(
    initialSelectedColumns
  );
  const [pendingInvestorType, setPendingInvestorType] = useState<string>(initialInvestorType);

  useEffect(() => {
    setPendingSelectedColumns(initialSelectedColumns);
  }, [initialSelectedColumns]);

  useEffect(() => {
    setPendingInvestorType(initialInvestorType);
  }, [initialInvestorType]);

  useEffect(() => {
    setPendingSelectedColumns(prevSelectedCols => {
      let newSelectedFields = isExplorer
        ? [...DEFAULT_SELECTED_COLUMNS_EXPLORER]
        : [...prevSelectedCols];
      const investorType = pendingInvestorType;

      if (investorType === 'buy') {
        if (!isExplorer && !newSelectedFields.includes('property_price_2bd'))
          newSelectedFields.push('property_price_2bd');
        if (!isExplorer && !newSelectedFields.includes('short_term_rental_yield_2bd'))
          newSelectedFields.push('short_term_rental_yield_2bd');
        newSelectedFields = newSelectedFields.filter(field =>
          isExplorer ? !['rent_gap'].includes(field) : !['rent_2bd', 'rent_gap_2bd'].includes(field)
        );
      } else if (investorType === 'rent') {
        if (!isExplorer && !newSelectedFields.includes('rent_2bd'))
          newSelectedFields.push('rent_2bd');
        if (!isExplorer && !newSelectedFields.includes('rent_gap_2bd'))
          newSelectedFields.push('rent_gap_2bd');
        newSelectedFields = newSelectedFields.filter(field =>
          isExplorer
            ? !['short_term_rental_yield'].includes(field)
            : !['property_price_2bd', 'short_term_rental_yield_2bd'].includes(field)
        );
      } else if (investorType === 'manage') {
        newSelectedFields = newSelectedFields.filter(field =>
          isExplorer
            ? !['short_term_rental_yield', 'rent_gap'].includes(field)
            : ![
                'property_price_2bd',
                'short_term_rental_yield_2bd',
                'rent_2bd',
                'rent_gap_2bd',
                'save',
              ].includes(field)
        );
      }
      return Array.from(new Set(newSelectedFields));
    });
  }, [pendingInvestorType]);

  const handleToggleColumn = (columnId: string) => {
    setPendingSelectedColumns(prevSelected =>
      prevSelected.includes(columnId)
        ? prevSelected.filter(id => id !== columnId)
        : [...prevSelected, columnId]
    );
  };

  const handleApply = () => {
    onApplyColumns(pendingSelectedColumns);
    onApplyInvestorType(pendingInvestorType);
    onClose();
  };

  const handleRestart = () => {
    setPendingSelectedColumns(
      isExplorer ? DEFAULT_SELECTED_COLUMNS_EXPLORER : DEFAULT_SELECTED_COLUMNS_SAVED_MARKET_LIST
    );
    setPendingInvestorType('buy');
  };

  const groupedColumns = (isExplorer ? ALL_COLUMNS_EXPLORER : ALL_COLUMNS_SAVED_MARKET_LIST).reduce(
    (acc, column) => {
      if (!acc[column.group]) {
        acc[column.group] = [];
      }
      acc[column.group].push(column);
      return acc;
    },
    {} as Record<string, Column[]>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Customize Columns
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: theme => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <InvestorTypeFilter
            selectedInvestorType={pendingInvestorType}
            onChange={setPendingInvestorType}
          />
          <Button
            variant="outlined"
            startIcon={<RestartIcon />}
            onClick={handleRestart}
            size="small"
          >
            Reset
          </Button>
        </Box>

        {Object.entries(groupedColumns).map(([groupName, columnsInGroup], groupIndex) => (
          <Box
            key={groupName}
            sx={{ mb: groupIndex < Number(Object.keys(groupedColumns).length) - 1 ? 2 : 0 }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
              {groupName}
            </Typography>
            <Grid container spacing={1}>
              {columnsInGroup.map(column => (
                <Grid item key={column.id}>
                  <Chip
                    label={column.label}
                    clickable
                    onClick={() => handleToggleColumn(column.id)}
                    color={pendingSelectedColumns.includes(column.id) ? 'primary' : 'default'}
                    variant={pendingSelectedColumns.includes(column.id) ? 'filled' : 'outlined'}
                    onDelete={
                      pendingSelectedColumns.includes(column.id)
                        ? () => handleToggleColumn(column.id)
                        : undefined
                    }
                    size="small"
                    sx={
                      pendingSelectedColumns.includes(column.id)
                        ? {
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                            },
                          }
                        : {}
                    }
                  />
                </Grid>
              ))}
            </Grid>
            {groupIndex < Number(Object.keys(groupedColumns).length) - 1 && (
              <Divider sx={{ mt: 2.5 }} />
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={pendingSelectedColumns.length === 0}
        >
          Apply Changes ({pendingSelectedColumns.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnPicker;
